import { useSelectionStore } from '@/stores/ui/selection';
import { zoomToObject } from '@/threejs/objectManager';
import * as THREE from 'three';
import { ref } from 'vue';
import { clearAllMeasurementGizmos, highLightMeasurement, isMeasurementActive } from './measurement';

export function useSelectionHandler() {
  const selectionStore = useSelectionStore();

  // Pointer tracking variables
  let pointerDownPosition = null;
  const pointerDownPosition3D = ref(null);
  const MOVEMENT_THRESHOLD = 5; // pixels

  // Double-click tracking
  let lastClickTime = 0;
  let lastClickedObject = null;
  const DOUBLE_CLICK_DELAY = 300; // milliseconds

  const isDragging = ref(false);
  const dragObject = ref(null);

  const currentCamera = ref(null);

  window.addEventListener('cameraChanged', (event) => {
    const newCamera = event.detail.camera;
    currentCamera.value = newCamera;
  });

  function setupSelectionEvents(renderer, camera, scene, cameraControls) {
    currentCamera.value = camera;
    const handlers = {
      pointerdown: (event) => {
        pointerDownPosition = { x: event.clientX, y: event.clientY };
        //  handleObjectDragStart(event, scene, cameraControls)
      },
      pointermove: (event) => {
        if (!isDragging.value) return;
        if (!dragObject.value) return;

        const rect = event.target.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      },
      pointerup: (event) => {
        if (!pointerDownPosition) return;
        // Calculate the distance moved
        const dx = event.clientX - pointerDownPosition.x;
        const dy = event.clientY - pointerDownPosition.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Only handle selection if movement was within threshold
        if (distance <= MOVEMENT_THRESHOLD) {
          const currentTime = Date.now();
          const timeSinceLastClick = currentTime - lastClickTime;

          // Check if this is a double-click
          if (timeSinceLastClick <= DOUBLE_CLICK_DELAY && lastClickedObject) {
            handleObjectDoubleClick(event, scene, lastClickedObject);
            lastClickTime = 0;
            lastClickedObject = null;
          } else {
            // Single click - store the clicked object for potential double-click
            const clickedObject = getClickedObject(event, scene);
            lastClickedObject = clickedObject;
            lastClickTime = currentTime;
            handleObjectSelection(event, scene);
          }
        }
        if (isDragging.value) {
          cameraControls.enabled = true;
          isDragging.value = false;
          dragObject.value = null;
        }

        // Reset pointer down position
        pointerDownPosition = null;
      },
    };

    // Add event listeners
    Object.entries(handlers).forEach(([event, handler]) => {
      renderer.domElement.addEventListener(event, handler);
    });

    // Return cleanup function
    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        renderer.domElement.removeEventListener(event, handler);
      });
    };
  }

  function handleObjectDragStart(event, scene, cameraControls) {
    return false
    const rect = event.target.getBoundingClientRect()
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1

    // Create raycaster
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(new THREE.Vector2(x, y), currentCamera.value)

    // Collect all objects with userData.uuid
    const selectableObjects = []
    scene.traverse((object) => {
      if (object.userData.dragHandle === true) {
        selectableObjects.push(object)
      }
    })
    // Find intersected objects only among selectable objects
    const intersects = raycaster.intersectObjects(selectableObjects, true)

    if (intersects.length > 0) {
      // Get the first intersected object
      const object = intersects[0].object

      pointerDownPosition3D.value = object.userData.surfaceCenter

      cameraControls.enabled = false
      isDragging.value = true
      dragObject.value = object
    }
  }

  /**
   * Gets the clicked object without selecting it
   * @param {Event} event - The pointer event
   * @param {THREE.Scene} scene - The Three.js scene
   * @returns {string|null} - The code of the clicked object or null
   */
  function getClickedObject(event, scene) {
    if (isMeasurementActive()) return null;

    const rect = event.target.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(new THREE.Vector2(x, y), currentCamera.value);

    const selectableObjects = [];
    scene.traverse((object) => {
      if (object.userData.selectable === true) {
        selectableObjects.push(object);
      }
    });

    function getRaycastableObjects(objects) {
      const result = [];
      objects.forEach((obj) => {
        obj.traverse((child) => {
          let isVisible = true;
          let current = child;
          while (current) {
            if (current.visible === false) {
              isVisible = false;
              break;
            }
            current = current.parent;
          }
          if (isVisible && !(child.type === 'Line2')) {
            result.push(child);
          }
        });
      });
      return result;
    }

    const filteredObjects = getRaycastableObjects(selectableObjects);
    raycaster.layers.set(0);
    raycaster.layers.enable(1); // Enable l
    const intersects = raycaster.intersectObjects(filteredObjects, false);

    if (intersects.length > 0) {
      const targetIntersect = intersects.find((intersect) => intersect.object.userData.isProjectorTarget);

      const cloneIntersect = intersects.find((intersect) => intersect.object.userData.clone === true);

      if (cloneIntersect) {
        console.log('Clicked on clone object:', cloneIntersect.object.userData.clonedCode);
        return cloneIntersect.object.userData.clonedCode;
      }

      if (targetIntersect && targetIntersect.object.parent.userData.projectorCode) {
        return targetIntersect.object.userData.projectorCode;
      }

      // Check for projector sprite click
      const projectorSpriteIntersect = intersects.find((intersect) => intersect.object.userData.isProjectorSprite);
      if (projectorSpriteIntersect && projectorSpriteIntersect.object.userData.projectorCode) {
        return projectorSpriteIntersect.object.userData.projectorCode;
      }

      const annotationTargetIntersect = intersects.find((intersect) => intersect.object.userData.isAnnotationTarget);
      if (annotationTargetIntersect && annotationTargetIntersect.object.userData.annotationCode) {
        return annotationTargetIntersect.object.userData.annotationCode;
      }

      const object = intersects[0].object;

      function findSelectableAncestor(obj) {
        if (!obj) return null;
        if (obj.userData.selectable === true) return obj;
        return findSelectableAncestor(obj.parent);
      }

      const selectableObject = findSelectableAncestor(object);

      if (selectableObject && selectableObject.userData.code) {
        return selectableObject.userData.code;
      }
    }
    return null;
  }

  /**
   * Handles double-click events on objects
   * @param {Event} event - The pointer event
   * @param {THREE.Scene} scene - The Three.js scene
   * @param {string} objectCode - The code of the double-clicked object
   */
  function handleObjectDoubleClick(event, scene, objectCode) {
    // Don't handle double-click if measurement is active
    if (isMeasurementActive()) return;

    zoomToObject(objectCode);
  }

  /**
   * Handles raycasting and object selection
   * @param {Event} event - The pointer event
   * @param {THREE.Camera} camera - The Three.js camera
   * @param {THREE.Scene} scene - The Three.js scene
   */
  function handleObjectSelection(event, scene) {
    // Don't handle selection if measurement is active
    if (isMeasurementActive()) return;
    // Get mouse position in normalized device coordinates (-1 to +1)
    const rect = event.target.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // Create raycaster
    const raycaster = new THREE.Raycaster();
    raycaster.layers.set(0);
    raycaster.layers.enable(1); // Enable layers 0 and 1 for selection
    raycaster.setFromCamera(new THREE.Vector2(x, y), currentCamera.value);

    // Collect all objects with userData.uuid
    const selectableObjects = [];
    scene.traverse((object) => {
      if (object.userData.selectable === true) {
        selectableObjects.push(object);
      }
    });

    function getRaycastableObjects(objects) {
      const result = [];

      objects.forEach((obj) => {
        obj.traverse((child) => {
          let isVisible = true;
          let current = child;
          while (current) {
            if (current.visible === false) {
              isVisible = false;
              break;
            }
            current = current.parent;
          }

          if (isVisible && !(child.type === 'Line2')) {
            result.push(child);
          }
        });
      });

      return result;
    }

    const filteredObjects = getRaycastableObjects(selectableObjects);
    const intersects = raycaster.intersectObjects(filteredObjects, false);

    if (intersects.length > 0) {
      // First check if any of the intersected objects is a projector target
      const targetIntersect = intersects.find((intersect) => intersect.object.userData.isProjectorTarget);
      if (targetIntersect) {
        const projectorCode = targetIntersect.object.parent.userData.projectorCode;

        if (projectorCode) {
          // Select the main projector (strip _clone suffix if present) and enable target transform mode
          const mainProjectorCode = projectorCode.replace(/_clone$/, '');

          // Enable translate mode if no transform type is set (must be set before selection for watchers)
          if (!selectionStore.selectedObjectTransformType) {
            selectionStore.selectedObjectTransformType = 'translate';
          }

          // Select with targetMode enabled atomically
          selectionStore.setSelectedObject(mainProjectorCode, {
            targetMode: true,
          });
          return;
        }
      }

      // Check if any of the intersected objects is a projector sprite
      const projectorSpriteIntersect = intersects.find((intersect) => intersect.object.userData.isProjectorSprite);

      if (projectorSpriteIntersect) {
        const projectorCode = projectorSpriteIntersect.object.userData.projectorCode;
        if (projectorCode) {
          // Select the main projector (strip _clone suffix if present) without target mode
          const mainProjectorCode = projectorCode.replace(/_clone$/, '');
          selectionStore.setSelectedObject(mainProjectorCode);
          return;
        }
      }

      const cloneIntersect = intersects.find((intersect) => intersect.object.userData.clone === true);

      if (cloneIntersect) {
        selectionStore.setSelectedObject(cloneIntersect.object.userData.clonedCode);
        return;
      }

      // Check if any of the intersected objects is an annotation target
      const annotationTargetIntersect = intersects.find((intersect) => intersect.object.userData.isAnnotationTarget);

      if (annotationTargetIntersect) {
        const annotationCode = annotationTargetIntersect.object.userData.annotationCode;
        if (annotationCode) {
          // Select the annotation (but don't auto-enable transform mode)
          selectionStore.setSelectedObject(annotationCode);
          return;
        }
      }

      // If no target found, get the first intersected object
      const object = intersects[0].object;

      // Recursively find the nearest selectable ancestor
      function findSelectableAncestor(obj) {
        if (!obj) return null;
        if (obj.userData.selectable === true) return obj;
        return findSelectableAncestor(obj.parent);
      }

      const selectableObject = findSelectableAncestor(object);

      if (selectableObject && selectableObject.userData.code) {
        // Clear measurement gizmos when selecting any object
        clearAllMeasurementGizmos();
        highLightMeasurement(null);
        selectionStore.setSelectedObject(selectableObject.userData.code);
      } else {
        selectionStore.clearSelection();
        clearAllMeasurementGizmos();
        highLightMeasurement(null);
      }
    } else {
      selectionStore.clearSelection();
      clearAllMeasurementGizmos();
      highLightMeasurement(null);
    }
  }

  return {
    setupSelectionEvents,
    handleObjectSelection,
    handleObjectDoubleClick,
  };
}
